import { DatePickerDropdown, Text, VSpacer } from '@/components/DesignSystem';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { QueryKeys } from '@/constants/QueryKeys';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { EnergyApi } from '@/utilities/api/EnergyApi';
import {
  ApiEnergyCustomer,
  ApiEnergyCustomerBehavior,
} from '@api/interfaces/ApiEnergyCustomer';
import { useTheme } from '@mui/material';
import { formatLocalDate } from '@shared/utilities';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

interface ReverseBehaviorDialogProps {
  behavior: ApiEnergyCustomerBehavior,
  customerName: string,
  farmerId: string,
  latestDay: string,
  onClose: () => void,
  onReversed: (updated: ApiEnergyCustomer) => void,
  programStartDate?: string,
}

export const ReverseBehaviorDialog = ({
  behavior,
  customerName,
  farmerId,
  latestDay,
  onClose,
  onReversed,
  programStartDate,
}: ReverseBehaviorDialogProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { openSnackbar } = useSnackbar();
  const completedDate = behavior.completion?.completedDate;
  const earliestDay = [completedDate, programStartDate]
    .filter((date): date is string => !!date)
    .sort()
    .pop();
  const [reversedDate, setReversedDate] = useState(latestDay);

  const { data: preview, isError: isPreviewError } = useQuery(
    [QueryKeys.GET_ENERGY_REVERSE_PREVIEW, farmerId, behavior.id],
    () => EnergyApi.previewReverse(farmerId, behavior.id),
    { cacheTime: 0, retry: false },
  );

  const { isLoading, mutate: reverse } = useMutation(
    () => EnergyApi.reverseBehavior(farmerId, behavior.id, { reversedDate }),
    {
      onError: (error: { message?: string }) => {
        openSnackbar(error.message || t(($) => $.energy.programModal.errors.generic));
      },
      onSuccess: (updated) => {
        onReversed(updated);
        openSnackbar(t(($) => $.energy.reverse.reversed));
        onClose();
      },
    },
  );

  const notEnrolled = t(($) => $.customers.details.notEnrolled);
  const resultingRank = preview?.resultingTier?.rank ?? -1;
  const isDrop = !!preview && resultingRank < (preview.currentTier?.rank ?? -1);

  return (
    <ConfirmDialog
      confirmText={t(($) => $.energy.reverse.confirm)}
      isConfirmDisabled={!reversedDate}
      isLoading={isLoading || (!preview && !isPreviewError)}
      onCancel={onClose}
      onConfirm={preview ? () => reverse() : undefined}
      open
      testID="reverse-dialog"
      title={t(($) => $.energy.reverse.title)}
    >
      <Text category="body-medium">
        {t(($) => $.energy.reverse.body, { customerName, name: behavior.name })}
      </Text>
      <VSpacer size="4" />
      <DatePickerDropdown
        label={t(($) => $.energy.reverse.reversedOn)}
        maxDate={latestDay}
        minDate={earliestDay}
        onChange={(date) => setReversedDate(date ? formatLocalDate(date) : '')}
        outOfRangeErrorMessage={earliestDay && t(($) => $.shared.inputValidation.dateOutOfRange, {
          end: latestDay,
          start: earliestDay,
        })}
        testID="reverse-date"
        value={reversedDate ? DateTime.fromISO(reversedDate).toJSDate() : undefined}
        width={200}
      />
      <VSpacer size="4" />
      <Text category="body-medium" testID="reverse-current-tier">
        {t(($) => $.energy.reverse.currentTier, {
          tier: preview?.currentTier?.name ?? notEnrolled,
        })}
      </Text>
      <Text
        category="body-medium"
        sx={{ color: isDrop ? theme.palette.error.main : undefined }}
        testID="reverse-resulting-tier"
      >
        {t(($) => $.energy.reverse.resultingTier, {
          tier: preview?.resultingTier?.name ?? notEnrolled,
        })}
      </Text>
      {isPreviewError && (
        <Text category="body-small" sx={{ color: 'error.main' }} testID="reverse-preview-error">
          {t(($) => $.energy.programModal.errors.generic)}
        </Text>
      )}
      <VSpacer size="4" />
      <Text category="body-small" sx={{ color: 'text.secondary' }}>
        {t(($) => $.energy.reverse.pointsNote)}
      </Text>
    </ConfirmDialog>
  );
};
